import React, { useEffect, useState } from 'react';
import { Button, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { DateCalendar } from '@mui/x-date-pickers';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { dateDisplayFormat, Dayjs, isInSameWeek } from '~libs/dayjs';

import Icon, { faChevronLeft, faChevronRight } from '~components/icon';

interface ComponentProps {
  weekStart?: number;
  initialDate?: Dayjs;
  onChange?: (value: Dayjs) => void;
  showQuickIcons?: boolean;
  showSelected?: boolean;
}

const WeekPicker: React.FC<ComponentProps> = ({
  weekStart = 1,
  initialDate,
  onChange,
  showQuickIcons = true,
  showSelected = true,
}: ComponentProps) => {
  useEffect(() => {
    // Set starting week day on mount
    dayjs.updateLocale('en', {
      weekStart,
    });
  }, []);

  const [hoveredDay, setHoveredDay] = useState<Dayjs | null>(null);
  const [value, setValue] = useState(initialDate || dayjs());

  useEffect(() => {
    if (initialDate && !initialDate.isSame(value, 'day')) {
      setValue(initialDate);
    }
  }, [initialDate]);

  const setChange = (value: Dayjs) => {
    setValue(value);
    onChange && onChange(value);
  };

  // Custom day component, sets classname to any day being hovered over or within the week selection.
  const Day = ({ day, selectedDay, hoveredDay, ...other }: any) => {
    const className = classNames('day', {
      hovered: isInSameWeek(day, hoveredDay),
      selected: isInSameWeek(day, selectedDay) && showSelected,
    });

    return <PickersDay {...other} className={className} day={day} sx={{ px: 2.5 }} disableMargin selected={false} />;
  };

  const start = value.weekday(0).format(dateDisplayFormat);
  const end = value.weekday(6).format(dateDisplayFormat);

  return (
    <div className="d-flex float-right">
      <UncontrolledDropdown className="ms-3 d-flex flex-row align-items-center me-1">
        {showQuickIcons && (
          <Button
            className="date-chevron d-flex align-items-center justify-content-center"
            onClick={() => setChange(dayjs(start).subtract(7, 'days'))}
          >
            <Icon icon={faChevronLeft} />
          </Button>
        )}
        <DropdownToggle size="sm" caret color="none" className="selectable mx-1">
          {start} - {end}
        </DropdownToggle>
        {showQuickIcons && (
          <Button
            className="date-chevron d-flex align-items-center justify-content-center"
            onClick={() => setChange(dayjs(start).add(7, 'days'))}
          >
            <Icon icon={faChevronRight} />
          </Button>
        )}
        <DropdownMenu>
          <DateCalendar
            value={value}
            onChange={setChange}
            className="week-picker"
            showDaysOutsideCurrentMonth
            slots={{ day: Day }}
            slotProps={{
              day: (state) => ({
                selectedDay: value,
                hoveredDay,
                onPointerEnter: () => setHoveredDay(state.day),
                onPointerLeave: () => setHoveredDay(null),
              }),
            }}
          />
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

export default WeekPicker;
